import { IdatabaseAuto } from '../../types/core-types';
import { validateData } from '../../utils/error-handlers';

/**
 * Abstract base class that provides common properties and validation for database entities.
 * This class should be extended by all domain models that represent database documents.
 *
 * @abstract
 * @class DatabaseAuto
 * @property {string} _id - The unique identifier for the database document
 * @property {Date} createdAt - The timestamp when the document was created
 * @property {Date} updatedAt - The timestamp when the document was last updated
 *
 * @example
 * ```typescript
 * class User extends DatabaseAuto {
 *   name: string;
 *
 *   constructor(data: IdatabaseAuto & { name: string }) {
 *     super(data);
 *     this.name = data.name;
 *   }
 * }
 *
 * const user = new User({
 *   _id: '123',
 *   name: 'John Doe',
 *   createdAt: new Date(),
 *   updatedAt: new Date()
 * });
 * ```
 */
export abstract class DatabaseAuto {
  readonly _id: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  /**
   * Creates a new instance of DatabaseAuto.
   *
   * @constructor
   * @param {IdatabaseAuto} data - The data object containing document properties
   * @throws {Error} If validation of required fields fails
   *
   * @example
   * const data = {
   *   _id: '507f1f77bcf86cd799439011',
   *   createdAt: '2023-01-01T00:00:00.000Z',
   *   updatedAt: '2023-01-01T00:00:00.000Z'
   * };
   * const instance = new MyModel(data);
   */
  constructor(data: IdatabaseAuto) {
    validateData(data, {
      _id: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Database ID must be a non-empty string'
      },
      createdAt: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const createdAt = v instanceof Date ? v : new Date(v);

          // Check date validity
          return !isNaN(createdAt.getTime());
        },
        message: 'Created at must be a valid date (Date object or ISO date string)'
      },
      updatedAt: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const updatedAt = v instanceof Date ? v : new Date(v);

          // Check date validity
          return !isNaN(updatedAt.getTime());
        },
        message: 'Updated at must be a valid date (Date object or ISO date string)'
      }
    });

    this._id = data._id;
    this.createdAt = new Date(data.createdAt);
    this.updatedAt = new Date(data.updatedAt);
  }
}
